import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  NotebookPen,
  NotebookText,
  Check,
  Plus,
  Loader2,
  LogIn,
  X,
  BookmarkPlus,
  Inbox,
} from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

/**
 * Botão reutilizável "Salvar em caderno" / "+Caderno" / "+Seguir" — peça
 * central do Ecossistema do Aluno (Fase A · MVP).
 *
 * Props
 * ─────
 * - target: { type: "livro"|"artigo"|"video"|"autor"|"ep_radio"|"questao",
 *             id: string,
 *             meta?: {} }   // snapshot rendido no dashboard sem JOIN
 * - label?: string          // texto opcional ao lado do ícone (default p/ type)
 * - variant?: "pill" | "compact" | "icon"
 * - className?: string      // override do wrapper para alinhar em listas
 *
 * Comportamentos
 * ──────────────
 * - Logged-out → link "Entrar para salvar".
 * - Já salvo  → ícone verde + tooltip "Salvo em N caderno(s)".
 * - Click abre popover ancorado:
 *      ▸ lista de cadernos do usuário com contagem;
 *      ▸ linha "Sem caderno (Minha Biblioteca)" para salvar solto
 *        (útil em "+ Seguir autor", onde não existe pasta);
 *      ▸ form inline para criar caderno novo e já salvar nele.
 */
export default function SaveToCaderno({
  target,
  label,
  variant = "pill",
  className = "",
}) {
  const { user, cadernos } = useAuth();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedIn, setSavedIn] = useState(null); // null = unknown, [] = none, [...ids] = salvo
  const [busyId, setBusyId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  // Posição absoluta do popover em coordenadas de viewport (fixed) —
  // resolve o bug em que cards com ``overflow: hidden`` (.card-ep em
  // /videos e na home) recortavam o popover, tornando o +Caderno
  // visualmente inerte mesmo quando funcionando. Iter130.
  const [popPos, setPopPos] = useState(null);

  const defaultLabel = label ?? (target.type === "autor" ? "+ Seguir" : "+Caderno");

  // Estado inicial: já salvo? (consultamos só quando o usuário existe).
  useEffect(() => {
    if (!user || !target?.id) return;
    cadernos
      .check(target.type, target.id)
      .then((r) => setSavedIn(r.saved ? r.caderno_ids || [] : []))
      .catch(() => setSavedIn([]));
  }, [user, target?.id, target?.type, cadernos]);

  // Carrega lista de cadernos quando abre o popover.
  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    cadernos
      .list()
      .then(setList)
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, user, cadernos]);

  // Fecha ao clicar fora — checa tanto o wrapper inline quanto o
  // popover portaled (que vive em ``document.body``).
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      const insideTrigger = wrapRef.current && wrapRef.current.contains(e.target);
      const insidePopover = popoverRef.current && popoverRef.current.contains(e.target);
      if (!insideTrigger && !insidePopover) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Calcula a posição do popover em coordenadas viewport assim que
  // ele abre — e re-calcula quando o usuário rola ou redimensiona.
  // Mantém o popover ancorado à direita do botão para preservar a
  // estética original (right: 0; top: button.bottom + 8).
  useLayoutEffect(() => {
    if (!open) return;
    const updatePos = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const POP_WIDTH = 288; // w-72 em px
      const GUTTER = 8;
      let right = window.innerWidth - rect.right;
      if (right < GUTTER) right = GUTTER;
      // Garante que o popover não saia da viewport pela esquerda.
      const leftLimit = window.innerWidth - POP_WIDTH - GUTTER;
      if (right > leftLimit) right = leftLimit;
      setPopPos({
        top: rect.bottom + GUTTER,
        right,
      });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open]);

  const isSaved = Array.isArray(savedIn) && savedIn.length > 0;

  const stop = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  // ── Logged-out: link de convite ─────────────────────────────────
  if (user === false) {
    const sharedCls =
      variant === "icon"
        ? "inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-700/60 bg-slate-950/60 text-slate-400 hover:text-sky-300 hover:border-sky-500/60 transition"
        : "inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-slate-700/60 bg-slate-950/40 text-xs font-medium text-slate-400 hover:text-sky-300 hover:border-sky-500/60 transition";
    return (
      <Link
        to="/conta"
        state={{ mode: "register" }}
        onClick={(e) => e.stopPropagation()}
        className={`${sharedCls} ${className}`}
        title="Entre para salvar em um caderno"
        data-testid={`save-caderno-login-${target.type}-${target.id}`}
      >
        <LogIn className="w-3.5 h-3.5" />
        {variant !== "icon" && <span>Entrar para salvar</span>}
      </Link>
    );
  }
  if (user === null) {
    // Estado de carregamento global do auth — esconde até saber.
    return null;
  }

  const saveTo = async (caderno_id, e) => {
    // Para defesa contra navegação acidental quando o popover é
    // renderizado dentro de um card clicável (ex: VideosPage → EpisodeCard
    // com ``onClick=goToEpisode``). O wrapper externo do popover já tem
    // ``onClick={stopPropagation}``, mas a navegação podia vazar via
    // mousedown/click race conditions em alguns navegadores.
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setBusyId(caderno_id || "__null__");
    try {
      await cadernos.save({
        target_type: target.type,
        target_id: target.id,
        caderno_id: caderno_id || null,
        target_meta: target.meta || {},
      });
      toast.success(
        caderno_id
          ? `Salvo em "${list.find((c) => c.id === caderno_id)?.name || "caderno"}"`
          : target.type === "autor"
          ? "Você está seguindo este autor"
          : "Salvo em Minha Biblioteca",
      );
      setSavedIn((prev) => Array.from(new Set([...(prev || []), caderno_id || null])));
      // Atualiza count do caderno na lista aberta.
      if (caderno_id) {
        setList((items) =>
          items.map((c) => (c.id === caderno_id ? { ...c, count: (c.count || 0) + 1 } : c)),
        );
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const createAndSave = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const c = await cadernos.create({ name });
      await cadernos.save({
        target_type: target.type,
        target_id: target.id,
        caderno_id: c.id,
        target_meta: target.meta || {},
      });
      toast.success(`Caderno "${c.name}" criado e item salvo`);
      setList((items) => [{ ...c, count: 1 }, ...items]);
      setSavedIn((prev) => Array.from(new Set([...(prev || []), c.id])));
      setNewName("");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  // ── Trigger visual ─────────────────────────────────────────────
  const triggerCls = (() => {
    if (variant === "icon") {
      return `inline-flex items-center justify-center w-8 h-8 rounded-full border transition ${
        isSaved
          ? "border-amber-400/60 bg-amber-500/10 text-amber-300"
          : "border-slate-700/60 bg-slate-950/60 text-slate-300 hover:text-amber-300 hover:border-amber-400/60"
      }`;
    }
    if (variant === "compact") {
      return `inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition ${
        isSaved
          ? "border-amber-400/60 bg-amber-500/10 text-amber-300"
          : "border-slate-700/60 bg-slate-950/40 text-slate-300 hover:text-amber-300 hover:border-amber-400/60"
      }`;
    }
    // pill (default)
    return `inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition ${
      isSaved
        ? "border-amber-400/70 bg-amber-500/10 text-amber-200"
        : "border-slate-700/60 bg-slate-950/40 text-slate-200 hover:text-amber-200 hover:border-amber-400/60"
    }`;
  })();

  const Icon = isSaved ? Check : variant === "icon" ? BookmarkPlus : NotebookPen;
  const visibleLabel = isSaved
    ? target.type === "autor"
      ? "Seguindo"
      : "Salvo"
    : defaultLabel;

  return (
    <div
      ref={wrapRef}
      className={`relative inline-block ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          stop(e);
          setOpen((v) => !v);
        }}
        className={triggerCls}
        aria-haspopup="menu"
        aria-expanded={open}
        title={
          isSaved
            ? `Salvo em ${savedIn.length} caderno(s)`
            : target.type === "autor"
            ? "Seguir autor"
            : "Salvar em um caderno"
        }
        data-testid={`save-caderno-open-${target.type}-${target.id}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {variant !== "icon" && <span>{visibleLabel}</span>}
      </button>

      {open && popPos && typeof document !== "undefined" && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: "fixed",
            top: `${popPos.top}px`,
            right: `${popPos.right}px`,
            zIndex: 9999,
          }}
          className="w-72 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-3 animate-in fade-in slide-in-from-top-1"
          onClick={(e) => e.stopPropagation()}
          data-testid={`save-caderno-popover-${target.type}-${target.id}`}
        >
          <div className="flex items-start justify-between mb-2">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
                Salvar em
              </span>
              <h3 className="text-sm font-semibold text-white leading-tight mt-0.5">
                {target.type === "autor" ? "Meus Professores" : "Um caderno seu"}
              </h3>
              {target.meta?.title && (
                <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1 max-w-[14rem]">
                  {target.meta.title}
                </p>
              )}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-white shrink-0 -mt-1 -mr-1 p-1"
              data-testid="save-caderno-close"
              aria-label="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Linha "sem caderno" (Minha Biblioteca) */}
          <button
            onClick={(e) => saveTo(null, e)}
            disabled={busyId === "__null__" || (Array.isArray(savedIn) && savedIn.includes(null))}
            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-xs transition mb-1 ${
              Array.isArray(savedIn) && savedIn.includes(null)
                ? "bg-emerald-500/10 text-emerald-300 cursor-default"
                : "text-slate-200 hover:bg-slate-800/70"
            }`}
            data-testid="save-caderno-pick-null"
          >
            {busyId === "__null__" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
            ) : Array.isArray(savedIn) && savedIn.includes(null) ? (
              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            ) : (
              <Inbox className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            )}
            <span className="flex-1 truncate">
              {target.type === "autor" ? "Seguir (sem caderno)" : "Minha Biblioteca"}
            </span>
            <span className="text-[10px] text-slate-500">solto</span>
          </button>

          {loading ? (
            <div className="text-slate-400 text-xs py-2 px-2.5">Carregando…</div>
          ) : (
            <ul className="space-y-0.5 max-h-[42vh] overflow-y-auto pr-0.5">
              {list.length === 0 && (
                <li className="text-[11px] text-slate-500 italic px-2.5 py-1">
                  Você ainda não tem cadernos — crie o primeiro abaixo.
                </li>
              )}
              {list.map((c) => {
                const already = Array.isArray(savedIn) && savedIn.includes(c.id);
                return (
                  <li key={c.id}>
                    <button
                      onClick={(e) => !already && saveTo(c.id, e)}
                      disabled={already || busyId === c.id}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-xs transition ${
                        already
                          ? "bg-emerald-500/10 text-emerald-300 cursor-default"
                          : "text-slate-200 hover:bg-slate-800/70"
                      }`}
                      data-testid={`save-caderno-pick-${c.id}`}
                    >
                      {busyId === c.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                      ) : already ? (
                        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      ) : (
                        <NotebookText
                          className="w-3.5 h-3.5 shrink-0"
                          style={{ color: c.color || "#f59e0b" }}
                        />
                      )}
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className="text-[10px] text-slate-500">{c.count || 0}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <form
            onSubmit={createAndSave}
            className="mt-2 pt-2 border-t border-slate-800 flex gap-1.5"
          >
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Novo caderno…"
              maxLength={80}
              className="flex-1 bg-slate-950 border border-slate-800 rounded-full px-3 py-1 text-xs text-white placeholder:text-slate-500 focus:border-amber-400/60 outline-none"
              data-testid="save-caderno-new-name"
            />
            <button
              type="submit"
              disabled={creating || !newName.trim()}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-500/20 text-amber-200 border border-amber-400/60 hover:bg-amber-500/30 disabled:opacity-40"
              data-testid="save-caderno-new-submit"
            >
              {creating ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Plus className="w-3 h-3" />
              )}
              Criar
            </button>
          </form>
        </div>,
        document.body,
      )}
    </div>
  );
}
